import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Trash, ShieldCheck, Timer } from "@phosphor-icons/react";

const CONTACT_EMAIL = "privacy@vexamos.com";
const APP_NAME = "Vexamos";
const COMPANY = "Vidya Vikas Technocrats Services (OPC) Pvt Ltd.";

/**
 * Public info page that the Google Play Console references as the "Data
 * deletion request URL". It explains both the in-app self-service delete
 * and the email fallback for users who cannot sign in.
 */
export default function DeleteAccount() {
  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="delete-account-page">
      <header className="border-b border-zinc-200 sticky top-0 bg-white z-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900" data-testid="delete-back-link">
            <ArrowLeft size={14} weight="bold" /> Home
          </Link>
          <div className="font-display text-lg font-bold tracking-tight">EXAM<span className="text-blue-600">/</span>OS</div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-12 py-12 lg:py-16">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-semibold">Your data · Your control</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-3">Delete your account</h1>
        <p className="text-sm text-zinc-600 max-w-2xl">
          You can permanently delete your {APP_NAME} account and all associated personal data at any time. This page explains what happens and how to do it.
        </p>

        <div className="grid md:grid-cols-2 gap-0 mt-10 border border-zinc-200">
          <Card icon={<Trash size={22} className="text-red-600" weight="regular" />} title="What gets deleted">
            <ul className="space-y-2 text-sm text-zinc-700">
              <li>Your profile (name, email, phone, avatar, bio)</li>
              <li>All exam attempts, answers &amp; violation logs</li>
              <li>Wallet balance &amp; transaction history</li>
              <li>Session tokens, notifications, proctor chats</li>
              <li>Pending OTP records &amp; help-desk messages you sent</li>
            </ul>
          </Card>
          <Card icon={<ShieldCheck size={22} className="text-emerald-600" weight="regular" />} title="What is kept (anonymized)">
            <ul className="space-y-2 text-sm text-zinc-700">
              <li>Certificates you were awarded (the student name/email fields are replaced with &ldquo;Deleted user&rdquo; so anyone verifying the QR still sees a valid credential without your PII)</li>
              <li>Aggregated, non-identifying analytics your institution used for reporting</li>
            </ul>
          </Card>
        </div>

        <section className="mt-14">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">Option 1 · Self-service in-app</div>
          <h2 className="font-display text-2xl font-bold tracking-tight mb-3">Delete from your Profile</h2>
          <ol className="text-sm text-zinc-700 space-y-2 list-decimal list-inside">
            <li>Open the {APP_NAME} web app or the mobile app and sign in.</li>
            <li>Navigate to <span className="font-mono">Profile</span> from the sidebar / bottom nav.</li>
            <li>Scroll to the <strong>Danger Zone</strong> section at the bottom.</li>
            <li>Click <strong>Delete my account</strong>, re-enter your password (skip for Google Sign-In), and type <span className="font-mono">DELETE</span> to confirm.</li>
            <li>Your account and personal data are removed immediately; you&rsquo;ll be signed out.</li>
          </ol>
          <div className="mt-4 inline-flex items-center gap-2 text-xs text-zinc-500 border border-zinc-200 px-3 py-2">
            <Timer size={14} /> The deletion is applied immediately. There is no 30-day recovery window &mdash; make sure you have downloaded any certificates before deleting.
          </div>
        </section>

        <section className="mt-12">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">Option 2 · Email request (if you can&rsquo;t sign in)</div>
          <h2 className="font-display text-2xl font-bold tracking-tight mb-3">Email us and we&rsquo;ll delete it</h2>
          <p className="text-sm text-zinc-700">
            If you can&rsquo;t sign in (forgotten password, lost email access, etc.), send us an email from the address you registered with:
          </p>
          <ul className="text-sm text-zinc-700 space-y-1 mt-3 list-disc list-inside">
            <li><strong>To:</strong> <a className="link-underline font-medium text-zinc-900" href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Delete my Vexamos account")}`} data-testid="delete-mailto">{CONTACT_EMAIL}</a></li>
            <li><strong>Subject:</strong> Delete my Vexamos account</li>
            <li><strong>Body:</strong> Include your full name, the registered email and the date you created the account.</li>
          </ul>
          <p className="text-sm text-zinc-600 mt-3">
            We respond within <strong>3 business days</strong> and complete the deletion within <strong>30 days</strong>. If any of the deletion needs to be delayed for legal or compliance reasons we&rsquo;ll tell you in the same reply.
          </p>
        </section>

        <div className="mt-14 border border-zinc-200 p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">Ready?</div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-zinc-700">Sign in and open the Danger Zone on your Profile page.</p>
            <Link
              to="/login?next=/app/profile"
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2.5 text-sm font-semibold hover:bg-zinc-800"
              data-testid="delete-signin-btn"
            >
              Sign in to continue
            </Link>
          </div>
        </div>

        <div className="mt-14 text-xs text-zinc-500">
          Operator: {COMPANY}. See our{" "}
          <Link to="/privacy" className="link-underline font-medium">Privacy Policy</Link>{" "}
          and{" "}
          <Link to="/terms" className="link-underline font-medium">Terms of Service</Link>{" "}
          for full details.
        </div>
      </main>
    </div>
  );
}

function Card({ icon, title, children }) {
  return (
    <div className="p-6 border-b md:border-b-0 md:[&:nth-child(odd)]:border-r border-zinc-200">
      <div className="w-10 h-10 border border-zinc-200 bg-zinc-50 flex items-center justify-center mb-4">{icon}</div>
      <div className="font-display text-lg font-bold tracking-tight mb-3">{title}</div>
      {children}
    </div>
  );
}
